import { Timestamp, deleteField } from 'firebase/firestore';

/**
 * Firestore type of a lead attribute, from its leadField typeField. Twin of
 * functions/lib/leadFieldValue.js in lead1-crm-cloud-functions, which writes
 * the same collection and picks the same instants.
 *
 * Here it covers `date` only. The twin also covers `boolean` because over
 * there the values come from external webhooks, which do send empty strings;
 * the one boolean field here comes from a checkbox.
 *
 * The rule used to be written by field name: `birthday` alone, and only in
 * updateLeadSaga. createLeadSaga wrote every date as the string the form
 * holds.
 */

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const FIRESTORE_TYPES = { date: 'Timestamp', boolean: 'Boolean' };

/**
 * Lives next to the rule that decides the type, not in the form: a label kept
 * away from its rule stops being true without anyone noticing.
 */
export const firestoreTypeOf = (typeField) =>
  FIRESTORE_TYPES[typeField] || 'String';

/**
 * A date is a calendar day, not an instant. Anchored to the local midnight of
 * whoever typed it, it sits on the previous day in UTC east of Greenwich, and
 * the web services format it inside a UTC runtime, so it would leave one day
 * early. Dates already stored are left alone.
 */
const utcMidnight = (year, month, day) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  // Date.UTC does not refuse the 31st of February: it answers the 3rd of
  // March. Shape-valid impossible dates reach here from the form and from
  // every webhook, and without reading the day back the lead would be saved
  // with a birthday nobody ever typed. null is what the caller already knows
  // how to handle.
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  )
    return null;

  return Timestamp.fromDate(date);
};

/** null when the value is not a date: the caller decides what to do. */
const asTimestamp = (value) => {
  // leadTransformer converts birthday and createdAt; any other date field
  // already stored as a Timestamp, like `firstContact`, reaches the store as
  // one, and treating it as unreadable would delete it on every save.
  if (value instanceof Timestamp) return value;

  if (value instanceof Date && !isNaN(value.getTime())) {
    // A date that is exactly UTC midnight is a stored calendar day — what
    // this file and normalizeLead write, handed back as a Date by
    // leadTransformer — and its day is the UTC one. Read with the local
    // getters, a browser west of Greenwich names the day before and every
    // save of an untouched lead moves it back by one. Any other instant is a
    // local midnight from a date picker or an older write, and its day is
    // the one the browser shows.
    const storedDay =
      value.getUTCHours() === 0 &&
      value.getUTCMinutes() === 0 &&
      value.getUTCSeconds() === 0 &&
      value.getUTCMilliseconds() === 0;
    return storedDay
      ? utcMidnight(
          value.getUTCFullYear(),
          value.getUTCMonth() + 1,
          value.getUTCDate(),
        )
      : utcMidnight(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }

  if (typeof value !== 'string' || value === '') return null;

  // Components come from the string: the form stores 'YYYY-MM-DD', which Date
  // reads as UTC midnight, and west of Greenwich its local getters would name
  // the previous day.
  const iso = ISO_DATE.exec(value);
  if (iso) return utcMidnight(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return utcMidnight(
    parsed.getFullYear(),
    parsed.getMonth() + 1,
    parsed.getDate(),
  );
};

/**
 * Only the keys whose value changes shape, to be merged over the raw ones.
 *
 * `deleteInvalid` tells the two writes apart. On update, a date field emptied
 * or filled in wrong has to go. On create it must not: addDocument rejects
 * deleteField().
 */
export const coerceLeadValues = (
  values,
  leadFieldsByName,
  { deleteInvalid = true } = {},
) => {
  const changed = {};

  Object.keys(values || {}).forEach((name) => {
    const leadField = leadFieldsByName.get(name);
    if (!leadField || leadField.typeField !== 'date') return;

    const value = values[name];
    if (value === undefined || value === null) return;

    const timestamp = asTimestamp(value);
    if (timestamp === value) return;
    if (timestamp) changed[name] = timestamp;
    else if (deleteInvalid) changed[name] = deleteField();
  });

  return changed;
};
